/**
 * CertAIn wordmark — chooses the correct rendition for the current
 * effective theme. The PNG masters from /Branding are the canonical
 * source; this component just picks the right one and sets sensible
 * height defaults.
 *
 * Naming caveat (carried from /Branding/project_branding.md):
 *   - logo-light.png renders Cert/n in OFF-WHITE → use on DARK surfaces.
 *   - logo-dark.png  renders Cert/n in NAVY      → use on LIGHT surfaces.
 *
 * I.e. "logo-{X}" reads as "for use on a {X} background." Counterintuitive
 * but matches the brand-canon filenames already shipped to /public.
 *
 * UI-v2 Phase 0: logo src and alt text driven by brand config module
 * (src/config/brand.ts) — behavior and resolved values identical.
 */
import { useTheme } from "@/components/theme/useTheme";
import { brand } from "@/config/brand";

interface Props {
  className?: string;
  /** Pixel height; defaults to 28px (matches the prototype top bar). */
  height?: number;
  /**
   * Force the rendition for an ALWAYS-fixed surface, ignoring the theme.
   * Some chrome (e.g. an always-dark sidebar) does not follow the light/dark
   * toggle; on those surfaces the theme-derived variant washes out in the
   * mismatched mode. Pass `surface="dark"` to always use the off-white logo
   * (`/logo-light.png`) and `surface="light"` for the navy one. Omit to follow
   * the effective theme (default). Founder ruling 2026-06-08.
   *
   * SCOPE — TWO RATIFIED SITES (HoD ruling 2026-06-08 + v2 amendment 2026-07-09):
   *   1. SA-ADMIN-CHROME (AdminShell): the internal super-admin Operator Console
   *      chrome uses a forced-dark logo cell to signal platform-operator vs tenant.
   *   2. V2-SIDEBAR (SidebarV2): the v2 tenant sidebar is a fixed surface that
   *      follows its OWN local `data-v2-dark` toggle (not `<html>`/effective theme).
   *      Without `surface`, effective==="dark" (the default) causes the off-white
   *      logo to render on a near-white v2-light sidebar — washed out. SidebarV2
   *      passes `surface={v2Dark ? "dark" : "light"}` so the mark tracks its OWN
   *      toggle. HoD real-render correction 2026-07-09.
   *
   * Do NOT use `surface` on any other tenant-facing surface — those must follow
   * the effective theme so the wordmark renders correctly in both v1 modes.
   */
  surface?: "dark" | "light";
}

export function Wordmark({
  className = "",
  height = 28,
  surface,
}: Props): JSX.Element {
  const { effective } = useTheme();
  // On a fixed surface, force the matching rendition; otherwise follow theme.
  const renderDark = surface ? surface === "dark" : effective === "dark";
  const src = renderDark ? brand.logo.dark : brand.logo.light;
  return (
    <img
      src={src}
      alt={brand.name}
      style={{ height: `${height}px` }}
      className={`w-auto select-none ${className}`}
      draggable={false}
    />
  );
}
